import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getSaftCountryBySlug } from '@/data/saftCountries';
import { getEinvCountryBySlug } from '@/data/einvoicingCountries';
import { getSolutionMeta } from '@/data/solutions/registry';

const SITE_URL = 'https://taxera.tech';
const SITE_NAME = 'Taxera Technologies';
const DEFAULT_IMAGE = 'https://taxera.tech/og-image.png';

const PAGE_META = {
  '/': {
    title: 'e-Invoicing, SAF-T & VAT Compliance in Your ERP | Taxera',
    description: 'Taxera embeds in SAP and other ERPs to automate e-Invoicing, SAF-T and VAT reporting in every country. No spreadsheets. Book a demo.',
  },
  '/calculator': {
    title: 'Tax Compliance Cost Calculator | Taxera',
    description: 'Find out what global tax compliance really costs you in vendors, systems and people. Two questions, instant estimate.',
  },
  '/solutions/saf-t': {
    title: 'SAF-T Reporting from Your ERP | Taxera',
    description: 'Generate, validate and submit SAF-T files straight from your ERP data, in every country. One platform, no spreadsheets. See how Taxera does it.',
  },
  '/solutions/e-invoicing': {
    title: 'e-Invoicing by Country: Rules & Deadlines | Taxera',
    description: 'e-Invoicing rules by country: who has to issue e-invoices, key dates, formats and how each system works. Plain-English guides from Taxera.',
  },
  '/products': {
    title: 'Hubs & Apps | Taxera',
    description: 'Explore Taxera\'s specialized compliance hubs: e-Invoicing, eCompliance, eReporting, Tax Reporting, and Tax Operations. One platform, every mandate, every country.',
  },
  '/platform': {
    title: 'Platform: Tax Compliance Inside Your ERP | Taxera',
    description: 'Native ERP middleware, two-way SAP BTP integration, and end-to-end ownership from your ERP to the tax authority. Discover the Taxera compliance engine.',
  },
  '/sap-s4hana': {
    title: 'SAP S/4HANA Tax Compliance | Taxera',
    description: 'Don\'t let tax compliance derail your S/4HANA migration. Taxera deploys on SAP BTP in 8-12 weeks, covering all mandates across all countries. 37,000+ companies migrating.',
  },
  '/sap-s4': {
    title: 'SAP S/4HANA Tax Compliance | Taxera',
    description: 'Don\'t let tax compliance derail your S/4HANA migration. Taxera deploys on SAP BTP in 8-12 weeks, covering all mandates across all countries. 37,000+ companies migrating.',
  },
  '/sap-s4hana/assessment': {
    title: 'S/4HANA Readiness Assessment | Taxera',
    description: 'Assess your compliance readiness for SAP S/4HANA across 8 critical dimensions. Free 2-minute quiz with personalized scorecard and downloadable PDF report.',
  },
  '/sap-s4hana/calculator': {
    title: 'TCO Calculator: SAP S/4HANA Compliance Savings | Taxera',
    description: 'Calculate your potential savings from consolidating compliance vendors during SAP S/4HANA migration. Interactive TCO model with real-time projections.',
  },
  '/sap-s4hana/whitepaper': {
    title: 'Whitepaper: Tax Compliance in the Age of S/4HANA | Taxera',
    description: 'The definitive guide for Tax Directors, CIOs, and CFOs navigating ERP transformation and regulatory compliance. Free download with executive summary and TCO model.',
  },
  '/insights': {
    title: 'Global Tax Tracker: Regulatory Intelligence | Taxera',
    description: 'Track e-Invoicing mandates, SAF-T requirements, VAT returns, and tax structures across 56 countries. Live regulatory intelligence updated weekly.',
  },
  '/news': {
    title: 'Regulatory News: AI-Curated Tax Compliance Updates | Taxera',
    description: 'AI-curated regulatory tax compliance news. e-Invoicing mandates, SAF-T requirements, VAT rules, and digital reporting updates from around the globe.',
  },
  '/blog': {
    title: 'Blog: Tax Technology Thought Leadership | Taxera',
    description: 'Thought leadership, regulatory analysis, and technical deep-dives for tax and IT leaders managing global compliance transformation.',
  },
  '/evaluate': {
    title: 'Compliance Diagnostic Assessment | Taxera',
    description: 'Assess your global tax compliance posture in 3 minutes. Choose your perspective: Tax Director, IT Director, or CFO. Get a personalized compliance scorecard.',
  },
  '/resources/videos': {
    title: 'Video Library: Brand Films & Shorts | Taxera',
    description: 'Watch Taxera\'s brand films and vertical shorts. Download 9:16 videos optimized for LinkedIn and social media. The Autumn \'26 and Summer \'26 series.',
  },
  '/partners': {
    title: 'Partner Program: Advisory, SI & Technology | Taxera',
    description: 'Join the Taxera Partner Program. We work with advisory firms, system integrators, and technology partners to deliver global tax compliance at scale.',
  },
  '/media': {
    title: 'Media & Announcements | Taxera',
    description: 'Press releases, partner announcements, and company news from Taxera Technologies GmbH.',
  },
  '/security': {
    title: 'Security & Trust: ISO 27001, GDPR, SAP BTP | Taxera',
    description: 'ISO 27001 certified, GDPR compliant, built on SAP BTP infrastructure. Taxera embeds security by design into every layer of the compliance platform.',
  },
  '/about': {
    title: 'About: Swiss Tax Technology | Taxera',
    description: 'Taxera Technologies GmbH. Global tax compliance technology from Switzerland. One vendor, one platform, every jurisdiction.',
  },
  '/contact': {
    title: 'Book a Demo or Contact Us | Taxera',
    description: 'Get in touch with the Taxera team. Schedule a demo, request pricing, or speak with an SAP compliance specialist.',
  },
  '/dashboard': {
    title: 'My Diagnostics | Taxera',
    description: 'View and compare your saved diagnostic results across Tax Director, IT Director, and CFO perspectives.',
  },
  '/login': {
    title: 'Sign In | Taxera',
    description: 'Sign in to your Taxera account to access diagnostics, saved reports, and compliance tools.',
  },
  '/privacy': {
    title: 'Privacy Policy | Taxera',
    description: 'Taxera Technologies GmbH privacy policy. How we collect, use, and protect your personal data under GDPR.',
  },
  '/terms': {
    title: 'Terms of Service | Taxera',
    description: 'Terms of service for using the Taxera Technologies platform and website.',
  },
  '/cookies': {
    title: 'Cookie Policy | Taxera',
    description: 'Taxera Technologies cookie policy. Types of cookies used and how to manage your preferences.',
  },
  '/imprint': {
    title: 'Imprint | Taxera',
    description: 'Legal information and company details for Taxera Technologies GmbH, Switzerland.',
  },
  '/settings': {
    title: 'Account Settings | Taxera',
    description: 'Manage your Taxera account profile, security preferences, and notification settings.',
  },
  '/admin': {
    title: 'Admin Portal | Taxera',
    description: 'Taxera internal admin portal for content management, user administration, and analytics.',
  },
  '/tracker': {
    title: 'Global Tax Tracker: Indirect Tax Changes by Country | Taxera',
    description: 'Track e-Invoicing mandates, SAF-T requirements, and VAT reforms across 56 countries. Free, always up to date.',
  },
  '/tracker/einvoicing': {
    title: 'Global e-Invoicing Mandates Tracker | Taxera',
    description: 'Track e-Invoicing mandates across 20+ countries — current status, upcoming deadlines, and compliance requirements.',
  },
  '/tracker/saft': {
    title: 'SAF-T Requirements Tracker | Taxera',
    description: 'Track SAF-T digital audit file requirements across Europe — country status, deadlines, and reporting formats.',
  },
  '/tracker/vat_returns': {
    title: 'VAT Returns & Reporting Tracker | Taxera',
    description: 'Track VAT return filing requirements and digital reporting mandates by country.',
  },
  '/tracker/vat_structure': {
    title: 'VAT Structure Tracker | Taxera',
    description: 'Track VAT structure regulations, rates, and group reporting requirements across jurisdictions.',
  },
  '/knowledge-base': {
    title: 'Country Tax Compliance Guides | Taxera',
    description: 'Jurisdiction-specific tax compliance guides. e-Invoicing rules, SAF-T requirements, VAT rates, and key deadlines by country.',
  },
  '/mandates': {
    title: 'Tax Mandate Deep Dives | Taxera',
    description: 'In-depth coverage of specific tax mandates and country requirements. Regulatory analysis, technical guidance, and readiness checklists.',
  },
  '/evaluate/business-case': {
    title: 'Build Your Tax Compliance Business Case | Taxera',
    description: 'Build a compelling business case for tax compliance automation. ROI calculator, cost comparison, and presentation-ready outputs.',
  },
  '/evaluate/benchmarks': {
    title: 'Tax Compliance Industry Benchmarks | Taxera',
    description: 'Compare your tax compliance maturity against industry benchmarks. See how you rank by sector, size, and region.',
  },
};

const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Taxera Technologies GmbH",
  "url": "https://taxera.tech",
  "logo": DEFAULT_IMAGE,
  "description": "Enterprise-grade tax compliance technology. One vendor, one platform, every jurisdiction.",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "CH",
  },
  "sameAs": [
    "https://www.linkedin.com/company/taxera"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "info@taxera.tech",
    "contactType": "sales",
  }
};

const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Taxera Technologies",
  "url": "https://taxera.tech",
  "description": "Global tax compliance platform for enterprise SAP environments.",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://taxera.tech/insights?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

const FAQ_SCHEMA_SAP = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What happens to tax compliance when migrating from SAP ECC to S/4HANA?",
      "acceptedAnswer": { "@type": "Answer", "text": "Tax configurations, reporting setups, and compliance workflows built in ECC don't automatically carry forward to S/4HANA. Without careful planning, organizations face filing failures and regulatory penalties during migration." }
    },
    {
      "@type": "Question",
      "name": "When does SAP ECC mainstream support end?",
      "acceptedAnswer": { "@type": "Answer", "text": "SAP ECC mainstream support officially ends on December 31, 2027. After that, only custom maintenance is available at significantly higher cost with no enhancements." }
    },
    {
      "@type": "Question",
      "name": "How long does Taxera implementation take during S/4HANA migration?",
      "acceptedAnswer": { "@type": "Answer", "text": "Taxera standard implementation takes 8-12 weeks and is designed to run in parallel with your S/4 project, not extending your critical path." }
    },
    {
      "@type": "Question",
      "name": "What TCO reduction can be expected with compliance platform consolidation?",
      "acceptedAnswer": { "@type": "Answer", "text": "Organizations typically achieve 40-60% TCO reduction by consolidating multiple compliance vendors onto the Taxera platform, eliminating 3-7 vendor contracts." }
    }
  ]
};

const FAQ_SCHEMA_GENERAL = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Taxera?",
      "acceptedAnswer": { "@type": "Answer", "text": "Taxera is an enterprise-grade cloud platform that helps multinational companies manage global indirect tax compliance, including e-Invoicing, SAF-T, VAT returns, and tax reconciliation." }
    },
    {
      "@type": "Question",
      "name": "Which ERP systems does Taxera integrate with?",
      "acceptedAnswer": { "@type": "Answer", "text": "Taxera integrates natively with SAP, Oracle, and Microsoft Dynamics, as well as other standard and non-standard ERP systems." }
    },
    {
      "@type": "Question",
      "name": "Is Taxera compliant with GDPR and data privacy regulations?",
      "acceptedAnswer": { "@type": "Answer", "text": "Yes. Taxera is ISO 27001 certified, GDPR compliant, and gives clients full flexibility over their data processing location and hyperscaler." }
    },
    {
      "@type": "Question",
      "name": "Which countries does Taxera cover?",
      "acceptedAnswer": { "@type": "Answer", "text": "Taxera covers tax compliance obligations across a growing number of countries and jurisdictions worldwide, and is continuously updated as new regulations are introduced globally." }
    },
    {
      "@type": "Question",
      "name": "What is e-Invoicing and why does it matter?",
      "acceptedAnswer": { "@type": "Answer", "text": "e-Invoicing is the digital exchange of invoices directly between companies and tax authorities. It is becoming mandatory across the EU and globally under regulations like ViDA." }
    },
    {
      "@type": "Question",
      "name": "How long does implementation take?",
      "acceptedAnswer": { "@type": "Answer", "text": "Implementation timelines vary by complexity, but Taxera is designed for rapid deployment with pre-built ERP connectors and a dedicated onboarding team." }
    },
    {
      "@type": "Question",
      "name": "Who is Taxera built for?",
      "acceptedAnswer": { "@type": "Answer", "text": "Taxera is built for tax directors, CFOs, and finance teams at multinational corporations who need a single, reliable hub for all indirect tax obligations." }
    }
  ]
};

const getPageSchemas = (pathname) => {
  const schemas = [ORGANIZATION_SCHEMA, WEBSITE_SCHEMA];

  if (pathname.startsWith('/sap-s4hana')) {
    schemas.push(FAQ_SCHEMA_SAP);
    schemas.push({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Taxera SAP S/4HANA Compliance Platform",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "SAP BTP",
      "description": "Cloud-native tax compliance platform built on SAP BTP for S/4HANA migrations.",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR", "description": "Contact for enterprise pricing" }
    });
  }

  if (pathname === '/' || pathname === '/products') {
    schemas.push(FAQ_SCHEMA_GENERAL);
  }

  if (pathname === '/blog') {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Taxera Blog",
      "description": "Tax technology thought leadership and regulatory analysis.",
      "publisher": { "@type": "Organization", "name": "Taxera Technologies GmbH" }
    });
  }

  return schemas;
};

// Canonical form: no trailing slash (except "/"), lower case (blog slugs keep their case).
export const cleanSeoPath = (raw) => {
  let p = (raw || '/').split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  return p.toLowerCase().startsWith('/blog/') ? '/blog/' + p.slice(6) : p.toLowerCase();
};

const SEO = () => {
  const { pathname: rawPathname } = useLocation();
  const pathname = cleanSeoPath(rawPathname);

  useEffect(() => {
    // Find meta: exact match first, then dynamic route patterns
    let meta = PAGE_META[pathname];
    let ogType = 'website';

    if (!meta) {
      // Dynamic route matching
      const solMeta = getSolutionMeta(pathname);
      if (solMeta) {
        meta = solMeta;
      } else if (pathname.startsWith('/solutions/e-invoicing/') && getEinvCountryBySlug(pathname.split('/')[3])) {
        const ec = getEinvCountryBySlug(pathname.split('/')[3]);
        meta = { title: ec.title, description: ec.description };
      } else if (pathname.startsWith('/solutions/saf-t/') && getSaftCountryBySlug(pathname.split('/')[3])) {
        const sc = getSaftCountryBySlug(pathname.split('/')[3]);
        meta = { title: sc.title, description: sc.description };
      } else if (pathname.startsWith('/tracker/') && pathname.split('/').length === 4) {
        // /tracker/{category}/{country}
        const parts = pathname.split('/');
        const catNames = { einvoicing: 'e-Invoicing', saft: 'SAF-T', vat_returns: 'VAT Returns', vat_structure: 'VAT Structure' };
        const cat = catNames[parts[2]] || parts[2];
        const cc = parts[3].toUpperCase();
        meta = { title: `${cc} ${cat} mandate: dates, scope and requirements | Taxera`, description: `Track ${cc}'s ${cat} compliance mandate — current status, key deadlines, scope, and what your business needs to do.` };
      } else if (pathname.startsWith('/tracker/')) {
        meta = PAGE_META[pathname] || { title: 'Global Tax Tracker | Taxera', description: 'Track tax mandates across 56 countries.' };
      } else if (pathname.startsWith('/knowledge-base/')) {
        const country = pathname.split('/')[2].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        meta = { title: `${country} tax compliance guide: e-Invoicing, SAF-T & VAT | Taxera`, description: `Complete guide to ${country}'s tax compliance requirements — e-Invoicing mandates, SAF-T reporting, VAT rules, key dates and practical guidance.` };
      } else if (pathname.startsWith('/mandates/')) {
        const slug = pathname.split('/')[2].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        meta = { title: `${slug} | Taxera`, description: `In-depth coverage of ${slug} — regulatory analysis, technical guidance, and readiness resources.` };
      } else if (pathname.startsWith('/industries/')) {
        const indNames = { manufacturing: 'Manufacturing', retail: 'Retail', automotive: 'Automotive', healthcare: 'Healthcare & Life Sciences', 'public-sector': 'Public Sector', logistics: 'Logistics & Supply Chain', energy: 'Energy & Utilities', finance: 'Finance & Insurance', food: 'Food & Beverage', travel: 'Travel & Hospitality', fashion: 'Fashion & Luxury', construction: 'Construction' };
        const key = pathname.split('/')[2];
        const name = indNames[key] || key.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        meta = { title: `${name} tax compliance: e-Invoicing & VAT automation | Taxera`, description: `Tax compliance automation built for ${name.toLowerCase()} — e-Invoicing, SAF-T, VAT returns across 30+ countries.` };
      } else if (pathname.startsWith('/resources/videos/')) {
        const vidNames = { 'keep-your-promises': 'Keep Your Promises', 'sunday': 'Sunday', 'how-it-works': 'How It Works', 'the-pickup': 'The Pickup', 'the-haircut': 'The Haircut', 'the-sunday-call': 'The Sunday Call' };
        const vid = pathname.split('/')[3];
        const name = vidNames[vid] || vid.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        meta = { title: `${name} | Taxera`, description: `Watch '${name}' from Taxera Technologies. Brand film series exploring the human side of tax compliance.` };
      }
    }

    if (!meta) meta = PAGE_META['/'];

    // Blog posts get og:type=article (set by BlogPostPage via window.__BLOG_META)
    if (pathname.startsWith('/blog/') && pathname !== '/blog') {
      ogType = 'article';
      if (window.__BLOG_META) {
        meta = { title: window.__BLOG_META.title, description: window.__BLOG_META.description };
      }
    }

    document.title = meta.title;
    setMetaTag('name', 'description', meta.description);
    setMetaProperty('og:title', meta.title);
    setMetaProperty('og:description', meta.description);
    setMetaProperty('og:type', ogType);
    setMetaProperty('og:site_name', SITE_NAME);
    setMetaProperty('og:image', (window.__BLOG_META?.image) || DEFAULT_IMAGE);
    setMetaProperty('og:url', `${SITE_URL}${pathname}`);
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', meta.title);
    setMetaTag('name', 'twitter:description', meta.description);
    setMetaTag('name', 'twitter:image', (window.__BLOG_META?.image) || DEFAULT_IMAGE);
    
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${SITE_URL}${pathname}`);
    setMetaTag('name', 'robots', pathname.startsWith('/admin') || pathname === '/dashboard' ? 'noindex, nofollow' : 'index, follow');

    document.querySelectorAll('script[data-seo-jsonld]').forEach(el => el.remove());
    const schemas = getPageSchemas(pathname);
    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', `schema-${i}`);
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

function setMetaTag(attr, key, content) {
  let tag = document.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setMetaProperty(property, content) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

export default SEO;
